import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SEOHead from "../components/SEOHead";

const TermsOfService = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const termsStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "利用規約",
    description:
      "わかば接骨院の利用規約。ウェブサイトの利用条件について説明しています。",
    url: "https://wakabaseto.jp/terms-of-service",
  };

  return (
    <>
      <SEOHead
        title="利用規約"
        description="わかば接骨院の利用規約。ウェブサイトの利用条件について説明しています。"
        keywords="利用規約,利用条件,わかば接骨院"
        canonical="/terms-of-service"
        structuredData={termsStructuredData}
      />
      <Box>
        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: "primary.light",
            py: { xs: 4, sm: 6, md: 8 },
            mb: { xs: 3, sm: 4, md: 6 },
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant={isMobile ? "h2" : "h1"}
              gutterBottom
              align="center"
              sx={{
                fontSize: {
                  xs: "2rem",
                  sm: isTablet ? "2.25rem" : "2.5rem",
                  md: "3rem",
                },
              }}
            >
              利用規約
            </Typography>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              align="center"
              sx={{
                mt: { xs: 1, md: 2 },
                color: "text.primary",
                fontSize: isTablet ? "1.1rem" : undefined,
              }}
            >
              ウェブサイト利用条件
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="lg">
          <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, mb: 6 }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              gutterBottom
              sx={{ mb: 4 }}
            >
              利用規約
            </Typography>

            <Typography variant="body1" paragraph>
              この利用規約（以下「本規約」）は、わかば接骨院（以下「当院」）が運営するウェブサイト（以下「本サイト」）の利用条件を定めるものです。本サイトをご利用になる方は、本規約に同意したものとみなします。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第1条（適用）
            </Typography>
            <Typography variant="body1" paragraph>
              1.
              本規約は、本サイトの利用に関する当院と利用者との間の権利義務関係を定めることを目的とし、利用者と当院との間の本サイトの利用に関わる一切の関係に適用されるものとします。
            </Typography>
            <Typography variant="body1" paragraph>
              2.
              当院が当サイトに掲載する情報、サービス等に関する個別の規約、諸規定等（以下「個別規定」）は本規約の一部を構成するものとし、個別規定が本規約と矛盾する場合には、個別規定が優先して適用されるものとします。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第2条（利用登録）
            </Typography>
            <Typography variant="body1" paragraph>
              1.
              本サイトの利用を希望する者は、本規約に同意の上、当院の定める方法によって利用登録を申請するものとします。
            </Typography>
            <Typography variant="body1" paragraph>
              2.
              当院は、利用登録の申請者に以下の事由があると判断した場合、利用登録の申請を承認しないことがあります。
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 3 }}>
              <Typography component="li" variant="body1" paragraph>
                虚偽の事項を届け出た場合
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                本規約に違反したことがある者からの申請である場合
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                その他、当院が利用登録を相当でないと判断した場合
              </Typography>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第3条（禁止事項）
            </Typography>
            <Typography variant="body1" paragraph>
              利用者は、本サイトの利用にあたり、以下の行為をしてはなりません。
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 3 }}>
              <Typography component="li" variant="body1" paragraph>
                法令または公序良俗に違反する行為
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                犯罪行為に関連する行為
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                当院のサーバーまたはネットワークの機能を破壊したり、妨害したりする行為
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                本サイトのサービスの運営を妨害するおそれのある行為
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                他の利用者に関する個人情報等を収集または蓄積する行為
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                他の利用者に成りすます行為
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                当院のサービスに関連して、反社会的勢力に対して直接または間接に利益を供与する行為
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                その他、当院が不適切と判断する行為
              </Typography>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第4条（本サイトの提供の停止等）
            </Typography>
            <Typography variant="body1" paragraph>
              1.
              当院は、以下のいずれかの事由があると判断した場合、利用者に事前に通知することなく本サイトの全部または一部の提供を停止または中断することができるものとします。
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 3 }}>
              <Typography component="li" variant="body1" paragraph>
                本サイトにかかるコンピュータシステムの保守点検または更新を行う場合
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                地震、落雷、火災、停電または天災などの不可抗力により、本サイトの提供が困難となった場合
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                その他、当院が本サイトの提供が困難と判断した場合
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              2.
              当院は、本サイトの提供の停止または中断により利用者または第三者に生じた損害について、一切の責任を負いません。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第5条（免責事項）
            </Typography>
            <Typography variant="body1" paragraph>
              1.
              当院は、本サイトに起因して利用者に生じたあらゆる損害について、当院の故意または重過失による場合を除き、一切の責任を負いません。
            </Typography>
            <Typography variant="body1" paragraph>
              2.
              当院は、本サイトに関して、利用者と他の利用者または第三者との間において生じた取引、連絡または紛争等について一切責任を負いません。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第6条（サービス内容の変更等）
            </Typography>
            <Typography variant="body1" paragraph>
              当院は、利用者に通知することなく、本サイトの内容を変更しまたは本サイトの提供を中止することができるものとし、これによって利用者に生じた損害について一切の責任を負いません。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第7条（利用規約の変更）
            </Typography>
            <Typography variant="body1" paragraph>
              当院は、必要と判断した場合には、利用者に通知することなくいつでも本規約を変更することができるものとします。なお、本規約の変更後、本サイトの利用を継続した場合には、変更後の規約に同意したものとみなします。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第8条（通知または連絡）
            </Typography>
            <Typography variant="body1" paragraph>
              利用者と当院との間の通知または連絡は、当院の定める方法によって行うものとします。当院は、利用者から、当院が別途定める方法に従った変更届け出がない限り、現在登録されている連絡先が有効なものとみなして当該連絡先へ通知または連絡を行い、これらは、発信時に利用者へ到達したものとみなします。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第9条（権利義務の譲渡の禁止）
            </Typography>
            <Typography variant="body1" paragraph>
              利用者は、当院の書面による事前の承諾なく、利用契約上の地位または本規約に基づく権利もしくは義務を第三者に譲渡し、または担保に供することはできません。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              第10条（準拠法・裁判管轄）
            </Typography>
            <Typography variant="body1" paragraph>
              1. 本規約の解釈にあたっては、日本法を準拠法とします。
            </Typography>
            <Typography variant="body1" paragraph>
              2.
              本サイトに関して紛争が生じた場合には、当院の本店所在地を管轄する裁判所を専属的合意管轄とします。
            </Typography>

            <Typography variant="body2" sx={{ mt: 4, color: "text.secondary" }}>
              制定日: 2024年12月14日
              <br />
              最終更新日: 2024年12月14日
            </Typography>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default TermsOfService;
